import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useLocation, useSearch } from "wouter";
import Layout from "@/components/layout/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Building,
  Plus,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronRight,
  Search,
  AlertCircle,
  Download,
} from "lucide-react";
import { useInsureds, useInsuredsStats, useSalesAgents, useFeatureFlag, exportInsuredsCsv } from "@/lib/api";
import type { AugmentedInsuredRow, InsuredSortField, SortDir } from "@shared/pagination";
import { useAgencyContext } from "@/lib/agencyContext";
import { FilterChip } from "@/components/FilterChip";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { AddInsuredDialog } from "@/components/insured/AddInsuredDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FilterField, FiltersButton, FiltersPanel } from "@/components/list-filters/ListFilters";
import { RETENTION_AGENT_TEAM } from "@shared/retentionAgents";
import { US_STATE_OPTIONS } from "@/lib/us-states";
import { useToast } from "@/hooks/use-toast";
import { trackEvent } from "@/lib/trackEvent";
import { FLAG_POLICIES_INSUREDS_CSV_EXPORT } from "@shared/feature-flags";

type ChipFilter = "inForce" | "expiring" | "lost";
const VALID_CHIP_FILTERS: ChipFilter[] = ["inForce", "expiring", "lost"];
const VALID_SORTS: InsuredSortField[] = ["customerName", "dotNumber", "activePolicyCount", "cycle", "status"];
const ALL = "__all__";

export default function Insureds() {
  const { selectedAgency, isAllAgencies } = useAgencyContext();
  const agencyId = isAllAgencies ? undefined : selectedAgency?.id;
  const [, navigate] = useLocation();
  const search = useSearch();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const { toast } = useToast();
  const { data: salesAgents } = useSalesAgents();
  const csvExportEnabled = useFeatureFlag(FLAG_POLICIES_INSUREDS_CSV_EXPORT);

  // CSR-674: pagination/filter/sort/search state lives in the URL.
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const rawStatus = params.get("status");
  const rawBucket = params.get("bucket") ?? rawStatus;
  const chipFilter: ChipFilter | null =
    rawBucket && (VALID_CHIP_FILTERS as string[]).includes(rawBucket) ? (rawBucket as ChipFilter) : null;
  const advancedStatus = rawStatus === "Active" || rawStatus === "Inactive" ? rawStatus : undefined;
  const salesAgent = params.get("salesAgent") || undefined;
  const retentionAgent = params.get("retentionAgent") || undefined;
  const state = params.get("state") || undefined;
  const rawBusinessType = params.get("businessType");
  const businessType =
    rawBusinessType === "New Business" || rawBusinessType === "Renewal" ? rawBusinessType : undefined;
  const qParam = params.get("q") || "";
  const rawSort = params.get("sort");
  const sortColumn: InsuredSortField | null =
    rawSort && (VALID_SORTS as string[]).includes(rawSort) ? (rawSort as InsuredSortField) : null;
  const sortDirection: SortDir = params.get("sortDir") === "asc" ? "asc" : "desc";
  const pageSize = 25;

  const [qInput, setQInput] = useState(qParam);
  const debouncedQ = useDebouncedValue(qInput, 250);
  const [addOpen, setAddOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const filtersButtonRef = useRef<HTMLButtonElement>(null);

  const setSearchParam = (key: string, value: string | null, resetPage = false) => {
    const next = new URLSearchParams(search);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    if (resetPage) next.delete("page");
    const qs = next.toString();
    navigate(qs ? `/insureds?${qs}` : "/insureds", { replace: false });
  };

  const setSearchParams = (updates: Record<string, string | null>) => {
    const next = new URLSearchParams(search);
    for (const [key, value] of Object.entries(updates)) {
      if (!value) next.delete(key);
      else next.set(key, value);
    }
    next.delete("page");
    const qs = next.toString();
    navigate(qs ? `/insureds?${qs}` : "/insureds", { replace: false });
  };

  useEffect(() => {
    if (debouncedQ === qParam) return;
    setSearchParam("q", debouncedQ || null, /*resetPage*/ true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  useEffect(() => {
    setQInput(qParam);
  }, [qParam]);

  const { data, isLoading, isPlaceholderData, isError, refetch } = useInsureds({
    agencyId,
    page,
    pageSize,
    nameQ: debouncedQ || undefined,
    bucket: chipFilter || undefined,
    advancedStatus,
    salesAgent,
    retentionAgent,
    state,
    businessType,
    sort: sortColumn ?? undefined,
    sortDir: sortDirection,
    includeUnmatched: true,
  });
  const { data: chipStats } = useInsuredsStats(agencyId);

  const insureds = data?.data || [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const activeFilterCount = [chipFilter, advancedStatus, salesAgent, retentionAgent, state, businessType].filter(
    Boolean,
  ).length;
  const setChipFilter = (next: ChipFilter | null) => {
    setSearchParams({
      bucket: next,
      status: rawStatus && (VALID_CHIP_FILTERS as string[]).includes(rawStatus) ? null : rawStatus,
    });
    trackEvent("Quick Filter Selected", { page: "Insureds", pill_name: next });
  };
  const setPage = (next: number) => setSearchParam("page", next > 1 ? String(next) : null);

  useEffect(() => {
    trackEvent("Insureds List Viewed", {
      default_filter_state: activeFilterCount === 0,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (debouncedQ && activeFilterCount > 0) {
      trackEvent("Search Used With Filters", {
        page: "Insureds",
        has_active_filters: true,
      });
    }
  }, [debouncedQ, activeFilterCount]);

  const toggleFilters = () => {
    const next = !filtersOpen;
    setFiltersOpen(next);
    trackEvent("Filters Panel Toggled", {
      page: "Insureds",
      state: next ? "open" : "closed",
    });
  };
  const focusFiltersButton = useCallback(() => filtersButtonRef.current?.focus(), []);
  const changeAdvanced = (name: string, value: string | null) => {
    setSearchParam(name, value, true);
    trackEvent("Advanced Filter Changed", {
      page: "Insureds",
      filter_name: name,
    });
  };
  const clearFilters = () => {
    setQInput("");
    setSearchParams({
      bucket: null,
      status: null,
      salesAgent: null,
      retentionAgent: null,
      state: null,
      businessType: null,
      q: null,
    });
    trackEvent("Filter Cleared", {
      page: "Insureds",
      filters_cleared_count: activeFilterCount,
    });
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const result = await exportInsuredsCsv({
        agencyId,
        nameQ: debouncedQ || undefined,
        bucket: chipFilter || undefined,
        advancedStatus,
        salesAgent,
        retentionAgent,
        state,
        businessType,
        sort: sortColumn ?? undefined,
        sortDir: sortDirection,
        includeUnmatched: true,
      });
      if (result.kind === "empty") {
        toast({
          title: "Nothing to export",
          description: "No insureds match the current filters.",
        });
        trackEvent("CSV Export Empty", {
          page: "Insureds",
          active_filter_count: activeFilterCount,
        });
        return;
      }
      trackEvent("CSV Exported", {
        page: "Insureds",
        result_count: total,
        active_filter_count: activeFilterCount,
        has_search: Boolean(debouncedQ),
      });
      if (activeFilterCount >= 2) {
        trackEvent("Filtered List Refined", {
          page: "Insureds",
          filter_types_used: activeFilterCount,
          exported: true,
        });
      }
    } catch {
      toast({
        title: "Export failed",
        description: "Please try again.",
        variant: "destructive",
      });
      trackEvent("CSV Export Failed", {
        page: "Insureds",
        result_count: total,
      });
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (column: InsuredSortField) => {
    let nextDir: SortDir;
    if (sortColumn === column) {
      nextDir = sortDirection === "asc" ? "desc" : "asc";
    } else {
      nextDir = "asc";
    }
    const next = new URLSearchParams(search);
    next.set("sort", column);
    next.set("sortDir", nextDir);
    next.delete("page");
    navigate(`/insureds?${next.toString()}`, { replace: false });
  };

  // Display name with the same fallback hierarchy used today.
  const displayName = (insured: AugmentedInsuredRow) => insured.name || insured.commercialName || "(unnamed insured)";

  const getCycleBadge = (insured: AugmentedInsuredRow) => {
    if (insured.unmatched) {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-500/10 text-yellow-700">
          Unmatched
        </span>
      );
    }
    if (insured.activePolicyCount > 0 && insured.hasUpcomingRenewal) {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-600">Renewal</span>
      );
    }
    if (insured.activePolicyCount > 0) {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-600">
          Active Customer
        </span>
      );
    }
    if (insured.hasRecentLoss) {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-500/10 text-orange-600">At Risk</span>
      );
    }
    return (
      <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-muted text-muted-foreground">Inactive</span>
    );
  };

  const getStatusBadge = (insured: AugmentedInsuredRow) => {
    if (insured.activePolicyCount > 0) {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-600">
          Active
        </span>
      );
    }
    return (
      <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-muted text-muted-foreground">Inactive</span>
    );
  };

  return (
    <Layout>
      <div className="flex flex-col gap-6" style={{ height: "calc(100vh - 7rem)" }}>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 shrink-0">
          <div>
            <h1 className="text-2xl font-extrabold font-heading text-foreground">Insureds</h1>
            <p className="text-muted-foreground mt-1">Manage your clients, leads, and prospects.</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <FiltersButton
              ref={filtersButtonRef}
              open={filtersOpen}
              activeCount={activeFilterCount}
              onClick={toggleFilters}
            />
            {activeFilterCount > 0 ? (
              <Button type="button" variant="ghost" onClick={clearFilters}>
                Clear
              </Button>
            ) : null}
            {csvExportEnabled ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={exporting}
                  aria-busy={exporting}
                  onClick={handleExport}
                  data-testid="button-export-insureds"
                >
                  {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                  {exporting ? "Exporting…" : "Export CSV"}
                </Button>
                <span className="sr-only" role="status" aria-live="polite">
                  {exporting ? "Exporting insureds CSV" : ""}
                </span>
              </>
            ) : null}
            <Button
              data-testid="insureds-add-button"
              onClick={() => setAddOpen(true)}
              className="rounded-2xl shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 transition-all font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Insured
            </Button>
          </div>
        </div>

        <FiltersPanel open={filtersOpen} onCloseFocus={focusFiltersButton}>
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <FilterChip
                label="In Force"
                count={chipStats?.inForce.count ?? 0}
                unit="insured"
                premium={chipStats?.inForce.premium ?? 0}
                selected={chipFilter === "inForce"}
                onClick={() => setChipFilter(chipFilter === "inForce" ? null : "inForce")}
                accent="green"
                tooltip="Insureds with at least one active policy — currently within its effective period."
                data-testid="chip-in-force"
              />
              <FilterChip
                label="Expiring Soon"
                count={chipStats?.expiring.count ?? 0}
                unit="insured"
                premium={chipStats?.expiring.premium ?? 0}
                selected={chipFilter === "expiring"}
                onClick={() => setChipFilter(chipFilter === "expiring" ? null : "expiring")}
                accent="yellow"
                tooltip="Insureds with at least one active policy expiring within the next 60 days."
                data-testid="chip-expiring"
              />
              <FilterChip
                label="Recently Lost"
                count={chipStats?.lost.count ?? 0}
                unit="insured"
                premium={chipStats?.lost.premium ?? 0}
                selected={chipFilter === "lost"}
                onClick={() => setChipFilter(chipFilter === "lost" ? null : "lost")}
                accent="red"
                tooltip="Insureds with at least one policy that expired or was cancelled in the last 60 days."
                data-testid="chip-lost"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
              <FilterField label="Status">
                <Select
                  value={advancedStatus ?? ALL}
                  onValueChange={(value) => changeAdvanced("status", value === ALL ? null : value)}
                >
                  <SelectTrigger aria-label="Insured status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All statuses</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Sales Agent">
                <Select
                  value={salesAgent ?? ALL}
                  onValueChange={(value) => changeAdvanced("salesAgent", value === ALL ? null : value)}
                >
                  <SelectTrigger aria-label="Sales agent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All sales agents</SelectItem>
                    {(salesAgents ?? []).map((name) => (
                      <SelectItem key={name} value={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Retention Agent">
                <Select
                  value={retentionAgent ?? ALL}
                  onValueChange={(value) => changeAdvanced("retentionAgent", value === ALL ? null : value)}
                >
                  <SelectTrigger aria-label="Retention agent">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All retention agents</SelectItem>
                    {RETENTION_AGENT_TEAM.map((name) => (
                      <SelectItem key={name} value={name}>
                        {name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="State">
                <Select
                  value={state ?? ALL}
                  onValueChange={(value) => changeAdvanced("state", value === ALL ? null : value)}
                >
                  <SelectTrigger aria-label="State">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All states</SelectItem>
                    {US_STATE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Business Type">
                <Select
                  value={businessType ?? ALL}
                  onValueChange={(value) => changeAdvanced("businessType", value === ALL ? null : value)}
                >
                  <SelectTrigger aria-label="Business type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ALL}>All business types</SelectItem>
                    <SelectItem value="New Business">New Business</SelectItem>
                    <SelectItem value="Renewal">Renewal</SelectItem>
                  </SelectContent>
                </Select>
              </FilterField>
            </div>

            <div className="relative w-full max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                placeholder="Search by insured name"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                className="pl-9"
                data-testid="input-search-insureds"
              />
            </div>
          </div>
        </FiltersPanel>

        {!filtersOpen ? (
          <div className="flex justify-end shrink-0">
            <div className="relative w-full max-w-xs">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                type="search"
                placeholder="Search by insured name"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                className="pl-9"
                data-testid="input-search-insureds"
              />
            </div>
          </div>
        ) : null}

        {isLoading && !isPlaceholderData ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <Card className="border-border shadow-none overflow-hidden flex-1 flex flex-col min-h-0">
            <div className="overflow-auto flex-1 min-h-0">
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-muted-foreground uppercase bg-muted border-b border-border sticky top-0 z-10">
                  <tr>
                    {(
                      [
                        ["customerName", "Insured"],
                        ["cycle", "Cycle"],
                        ["status", "Status"],
                        ["activePolicyCount", "Active Policies"],
                      ] as const
                    ).map(([col, label]) => (
                      <th
                        key={col}
                        className="px-6 py-4 font-semibold cursor-pointer select-none hover:text-foreground transition-colors"
                        onClick={() => handleSort(col)}
                      >
                        <span className="inline-flex items-center gap-1">
                          {label}
                          {sortColumn === col ? (
                            sortDirection === "asc" ? (
                              <ArrowUp className="w-3.5 h-3.5" />
                            ) : (
                              <ArrowDown className="w-3.5 h-3.5" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />
                          )}
                        </span>
                      </th>
                    ))}
                    <th className="px-6 py-4 w-12" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border/50">
                  {insureds.map((insured) => (
                    <tr
                      key={insured.id ?? `unmatched:${displayName(insured)}`}
                      onClick={() => {
                        // CSR-669: only real insureds are navigable. Unmatched
                        // rows have no FK target.
                        if (insured.id) navigate(`/insureds/${insured.id}`);
                      }}
                      className={
                        insured.unmatched ? "transition-colors" : "hover:bg-muted/30 transition-colors cursor-pointer"
                      }
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center text-muted-foreground shrink-0">
                            <Building className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-semibold text-foreground">{displayName(insured)}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {insured.dotNumber ? `DOT: ${insured.dotNumber}` : insured.email || ""}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">{getCycleBadge(insured)}</td>
                      <td className="px-6 py-4">{getStatusBadge(insured)}</td>
                      <td className="px-6 py-4 font-medium text-muted-foreground">{insured.activePolicyCount}</td>
                      <td className="px-6 py-4">
                        {insured.id && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                      </td>
                    </tr>
                  ))}
                  {/* CSR-1989 — a failed load must never render as an empty
                      result. It previously fell through to "No insureds found.",
                      so users concluded search was broken and worked around it by
                      logging out and back in. */}
                  {isError && (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center" data-testid="insureds-load-error">
                        <AlertCircle className="w-8 h-8 mx-auto mb-3 text-destructive" aria-hidden="true" />
                        <p className="text-base font-medium text-foreground">Couldn't load insureds</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          This is a loading failure, not an empty result. Your insureds are still here.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-4"
                          onClick={() => refetch()}
                          data-testid="button-retry-insureds"
                        >
                          Try again
                        </Button>
                      </td>
                    </tr>
                  )}
                  {!isError && insureds.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">
                        No insureds found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-3 border-t shrink-0">
                <p className="text-sm text-muted-foreground">
                  {((page - 1) * pageSize + 1).toLocaleString()}&ndash;
                  {Math.min(page * pageSize, total).toLocaleString()} of {total.toLocaleString()}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(1)}
                    disabled={page === 1 || isPlaceholderData}
                  >
                    First
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1 || isPlaceholderData}
                  >
                    Previous
                  </Button>
                  <span className="text-sm px-3">
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages || isPlaceholderData}
                  >
                    Next
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(totalPages)}
                    disabled={page === totalPages || isPlaceholderData}
                  >
                    Last
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
      <AddInsuredDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        onCreated={(id) => {
          setAddOpen(false);
          window.location.assign(`/insureds/${id}`);
        }}
      />
    </Layout>
  );
}
